import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorNotCombinable - The not-combinable error element.
 * @property {HTMLElement} cartDiscountErrorAlreadyApplied - The already-applied error element.
 * @property {HTMLElement} cartDiscountErrorNetwork - The network/unexpected error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorNotCombinable',
    'cartDiscountErrorAlreadyApplied',
    'cartDiscountErrorNetwork',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      this.#clearDiscountErrors();

      const existingDiscounts = await this.#existingDiscounts(abortController.signal);

      if (existingDiscounts.some((code) => code.toLowerCase() === discountCodeValue.toLowerCase())) {
        this.#handleDiscountError('already_applied');
        return;
      }

      const data = await this.#submitDiscounts([...existingDiscounts, discountCodeValue], abortController.signal);

      // Codes that were on the cart before this submission and are no longer applicable were
      // displaced by Shopify, so put the cart back the way the customer had it before saying anything.
      const displacedDiscounts = existingDiscounts.filter((code) => !this.#isApplicable(data.discount_codes, code));
      const newCodeApplied = this.#isApplicable(data.discount_codes, discountCodeValue);

      if (displacedDiscounts.length > 0) {
        const restored = await this.#submitDiscounts(existingDiscounts, abortController.signal);

        if (!existingDiscounts.every((code) => this.#isApplicable(restored.discount_codes, code))) {
          this.#handleDiscountError('network');
          return;
        }

        // The typed code only applied by knocking the existing one off, so the two do not combine.
        // Otherwise nothing applied at all, which is the ordinary failure.
        if (!newCodeApplied) discountCode.value = '';
        this.#handleDiscountError(newCodeApplied ? 'not_combinable' : 'discount_code');
        return;
      }

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      // A new submission aborts the in-flight one, which is not a failure to report.
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.#handleDiscountError('network');
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = await this.#existingDiscounts(abortController.signal);
      // The pill carries the discount's canonical title while the cart echoes the code as the
      // customer typed it, so the two can differ in case.
      const index = existingDiscounts.findIndex((code) => code.toLowerCase() === discountCode.toLowerCase());
      if (index === -1) return;

      existingDiscounts.splice(index, 1);

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      // Something went wrong, should we retry?
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'not_combinable' | 'already_applied' | 'network' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorNotCombinable,
      cartDiscountErrorAlreadyApplied,
      cartDiscountErrorNetwork,
      cartDiscountErrorShipping,
    } = this.refs;
    const target = {
      discount_code: cartDiscountErrorDiscountCode,
      not_combinable: cartDiscountErrorNotCombinable,
      already_applied: cartDiscountErrorAlreadyApplied,
      network: cartDiscountErrorNetwork,
      shipping: cartDiscountErrorShipping,
    }[type];
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Hides the discount error container and every message inside it.
   */
  #clearDiscountErrors() {
    const { cartDiscountError } = this.refs;
    cartDiscountError.classList.add('hidden');
    for (const text of cartDiscountError.querySelectorAll('.cart-discount__error-text')) {
      text.classList.add('hidden');
    }
  }

  /**
   * Submits a set of discount codes to the cart and returns the parsed cart response.
   *
   * @param {string[]} codes - The discount codes to submit.
   * @param {AbortSignal} signal - The signal for the request.
   */
  async #submitDiscounts(codes, signal) {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: codes.join(','),
        sections: [this.dataset.sectionId],
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, {
      ...config,
      signal,
    });

    return response.json();
  }

  /**
   * Whether a code came back applicable in a cart response.
   *
   * @param {{ code: string; applicable: boolean; }[]} discountCodes - The response's discount codes.
   * @param {string} code - The discount code to look for.
   * @returns {boolean}
   */
  #isApplicable(discountCodes, code) {
    return Boolean(discountCodes.find((discount) => discount.code === code && discount.applicable === true));
  }

  /**
   * Returns an array of the discount codes currently on the cart.
   *
   * Read from the cart itself rather than the rendered pills: a shipping-class discount renders
   * no pill, so reading the DOM omits it and the next submission drops it off the cart. Codes the
   * cart reports as not applicable are left out — they discount nothing, and carrying them would
   * make them look permanently displaced.
   *
   * @param {AbortSignal} signal - The signal for the request.
   * @returns {Promise<string[]>}
   */
  async #existingDiscounts(signal) {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { signal, cache: 'no-store' });
    const { discount_codes: discountCodes } = await response.json();

    return discountCodes
      .filter((/** @type {{ code: string; applicable: boolean; }} */ discount) => discount.applicable === true)
      .map((/** @type {{ code: string; applicable: boolean; }} */ discount) => discount.code);
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
